import React, { useCallback, useEffect, useState } from "react";
import {
  collection,
  addDoc,
  getDocs,
  deleteDoc,
  doc,
  updateDoc,
  getDoc,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { db, auth, storage } from "../config/firebaseConfig";

// Utility: extract path from downloadURL
const getPathFromUrl = (url) => {
  try {
    const base = "o/";
    const query = "?";
    const start = url.indexOf(base) + base.length;
    const end = url.indexOf(query);
    return decodeURIComponent(url.substring(start, end));
  } catch {
    return null;
  }
};

const noticesCollectionRef = collection(db, "notices");

const NoticeBoard = () => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);
  const [notices, setNotices] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [fetching, setFetching] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        try {
          const userRef = doc(db, "users", currentUser.uid);
          const userSnap = await getDoc(userRef);
          if (userSnap.exists()) {
            const userData = userSnap.data();
            if (
              (userData.role && userData.role.toLowerCase() === "admin") ||
              (Array.isArray(userData.roles) &&
                userData.roles.includes("admin"))
            ) {
              setIsAdmin(true);
            }
          }
        } catch (err) {
          console.error("Error checking admin:", err);
        }
      } else {
        setIsAdmin(false);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const fetchNotices = useCallback(async () => {
    setFetching(true);
    const data = await getDocs(noticesCollectionRef);
    const noticesList = data.docs.map((doc) => ({
      ...doc.data(),
      id: doc.id,
    }));
    setNotices(noticesList);
    setFetching(false);
  }, []);

  useEffect(() => {
    if (isAdmin) fetchNotices();
  }, [isAdmin, fetchNotices]);

  const handleUpload = async () => {
    if (!title || !description) {
      alert("⚠️ Please fill all fields!");
      return;
    }

    setSaving(true);
    let imageUrl = null;

    if (file) {
      const storageRef = ref(storage, `notices/${Date.now()}_${file.name}`);
      const uploadTask = uploadBytesResumable(storageRef, file);

      uploadTask.on(
        "state_changed",
        (snapshot) => {
          const progress =
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress(progress);
        },
        (error) => {
          console.error("Upload error:", error);
          setUploadProgress(0);
          setSaving(false);
        },
        async () => {
          imageUrl = await getDownloadURL(uploadTask.snapshot.ref);
          await saveNotice(imageUrl);
        }
      );
      return;
    }

    await saveNotice(null);
  };

  const saveNotice = async (imageUrl) => {
    try {
      if (editingId) {
        const noticeDoc = doc(db, "notices", editingId);
        await updateDoc(noticeDoc, { title, description, imageUrl });
      } else {
        await addDoc(noticesCollectionRef, {
          title,
          description,
          imageUrl,
          date: new Date().toLocaleDateString(),
        });
      }
    } catch (err) {
      console.error("Error saving notice:", err);
    } finally {
      setTitle("");
      setDescription("");
      setFile(null);
      setUploadProgress(0);
      setSaving(false);
      setEditingId(null);
      fetchNotices();
    }
  };

  const handleDelete = async (id, imageUrl) => {
    await deleteDoc(doc(db, "notices", id));
    if (imageUrl) {
      try {
        const path = getPathFromUrl(imageUrl);
        if (path) {
          const fileRef = ref(storage, path);
          await deleteObject(fileRef);
        }
      } catch (error) {
        console.error("Error deleting file:", error);
      }
    }
    fetchNotices();
  };

  const handleEdit = (notice) => {
    setTitle(notice.title);
    setDescription(notice.description);
    setEditingId(notice.id);
  };

  if (loading) {
    return (
      <p className="text-center text-gray-600 mt-8">🔍 Checking access...</p>
    );
  }

  if (!isAdmin) {
    return (
      <div className="p-6 text-center text-red-600 font-semibold bg-red-50 rounded shadow-md mt-8 mx-4">
        🚫 You do not have permission to access this page.
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h2 className="text-3xl font-bold mb-6 text-center text-blue-700">
        📌 Notice Board (Admin)
      </h2>

      {/* Form */}
      <div className="bg-white shadow-md rounded-lg p-6 mb-8 border border-gray-200">
        <input
          type="text"
          placeholder="Title"
          className="border border-gray-300 p-3 w-full mb-4 rounded focus:outline-none focus:ring-2 focus:ring-blue-400"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />

        <textarea
          placeholder="Description"
          className="border border-gray-300 p-3 w-full mb-4 rounded h-28 resize-none focus:outline-none focus:ring-2 focus:ring-blue-400"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />

        <div className="flex flex-col items-center">
          {/* File input */}
          <input
            type="file"
            accept="image/*,video/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation"
            className="mb-4"
            onChange={(e) => setFile(e.target.files[0])}
          />

          {/* Preview if file is selected */}
          {file && (
            <div className="w-40 h-40 border rounded-lg overflow-hidden flex items-center justify-center bg-gray-50 p-2">
              {file.type.startsWith("image") && (
                <img
                  src={URL.createObjectURL(file)}
                  alt="Preview"
                  className="w-full h-full object-contain"
                />
              )}

              {file.type.startsWith("video") && (
                <video
                  src={URL.createObjectURL(file)}
                  controls
                  className="w-full h-full object-contain"
                />
              )}

              {!file.type.startsWith("image") &&
                !file.type.startsWith("video") && (
                  <div className="flex flex-col items-center justify-center text-center text-gray-700">
                    📄 <span className="text-sm mt-1">{file.name}</span>
                  </div>
                )}
            </div>
          )}
        </div>

        {uploadProgress > 0 && (
          <div className="w-full bg-gray-200 rounded-full h-2.5 mb-4 overflow-hidden">
            <div
              className="bg-blue-500 h-2.5 rounded-full transition-all duration-300"
              style={{ width: `${uploadProgress}%` }}
            ></div>
          </div>
        )}

        <button
          onClick={handleUpload}
          disabled={saving}
          className={`w-full text-white px-5 py-3 rounded-lg font-semibold transition ${
            saving
              ? "bg-gray-400 cursor-not-allowed"
              : "bg-blue-600 hover:bg-blue-700"
          }`}
        >
          {saving
            ? "⏳ Saving..."
            : editingId
            ? "✏️ Update Notice"
            : "📤 Upload Notice"}
        </button>
      </div>

      {/* Notices */}
      <h3 className="text-xl font-semibold mb-4 text-gray-700">
        📋 All Notices
      </h3>
      {fetching ? (
        <p className="text-center text-gray-600">⏳ Loading notices...</p>
      ) : notices.length === 0 ? (
        <p className="text-center text-gray-500">No notices available.</p>
      ) : (
        <div className="grid gap-6 sm:grid-cols-1 md:grid-cols-2">
          {notices.map((notice) => (
            <div
              key={notice.id}
              className="bg-white shadow-lg rounded-lg p-5 flex flex-col justify-between hover:shadow-xl transition"
            >
              <div>
                <h4 className="font-bold text-lg text-blue-600">
                  {notice.title}
                </h4>
                <p className="text-gray-700 mt-2">{notice.description}</p>
                <span className="text-sm text-gray-400 mt-2 block">
                  {notice.date}
                </span>
                {notice.imageUrl && (
                  <a
                    href={notice.imageUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block text-blue-500 underline mt-2 hover:text-blue-700"
                  >
                    📎 View File
                  </a>
                )}
              </div>
              <div className="flex space-x-2 mt-4">
                <button
                  onClick={() => handleEdit(notice)}
                  className="flex-1 bg-yellow-400 px-3 py-2 rounded-lg font-medium text-white hover:bg-yellow-500 transition"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(notice.id, notice.imageUrl)}
                  className="flex-1 bg-red-500 px-3 py-2 rounded-lg font-medium text-white hover:bg-red-600 transition"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default NoticeBoard;
